/**
 * Vendor portal unread-notifications count as a react-query consumer.
 *
 * The header bell used to poll with its own useState + setInterval, which
 * meant the realtime WebSocket (which invalidates ['notifications','unread']
 * on a worker-created push) could not tick the badge — the two lived in
 * different worlds. Backing the bell with this query makes it a genuine
 * consumer: a WS push invalidates the key and the badge updates instantly,
 * while the 60s refetchInterval remains as a fallback when the socket is
 * down (dev, proxy without WS upgrade, Redis outage).
 *
 * Mirrors useMessagesUnread. Stale-while-revalidate is fine — the badge can
 * be off by up to 60s and nobody cares; opening /notifications and marking
 * read invalidates the key for an immediate update.
 */
import { useQuery } from '@tanstack/react-query';
import { notificationsApi } from '../api/notifications';
import { useAuth } from '../contexts/AuthContext';

export function useNotificationsUnread(enabled = true) {
  const { isAuthenticated } = useAuth();
  const q = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationsApi.unreadCount(),
    enabled: isAuthenticated && enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
  return q.data ?? 0;
}
